<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @shown="focusNewSku"
            @hidden="hidden"
            @ok="duplicatePublication"
        >
            <template #modal-title>
                Duplicate Publication
            </template>

            <div v-if="loading_publications">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
                <b-form-group
                    label="Publication"
                    label-for="publication"
                    :class="{'is-invalid': validating && !selected_publication}"
                >
                    <multiselect
                        v-model="selected_publication"
                        :options="publications"
                        label="title"
                        track-by="title"
                        @select="setSelectedPublication"
                    />

                    <b-form-invalid-feedback :state="validating && !selected_publication ? false : null">
                        Publication is required
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>

            <b-form-group
                label="SKU"
                label-for="new_sku"
            >
                <b-form-input
                    ref="new-sku"
                    :class="{'is-valid': sku_available, 'is-invalid': validating && !new_sku || sku_available == false}"
                    v-model.trim="new_sku"
                    @input="formatSku(new_sku)"
                />

                <b-form-text>Enter a SKU for the duplicated copy of this publication</b-form-text>

                <b-form-valid-feedback>
                    SKU is available
                </b-form-valid-feedback>

                <b-form-invalid-feedback v-if="sku_available == false">
                    SKU is unavailable
                </b-form-invalid-feedback>

                <b-form-invalid-feedback v-if="validating && !new_sku">
                    SKU is required
                </b-form-invalid-feedback>

            </b-form-group>

            <b-form-group
                label="Title"
                label-for="title"
            >
                <b-form-input
                    ref="new-title"
                    v-model="new_title"
                    :state="validating && !new_title ? false : null"
                />

                <b-form-text>Enter a new title for the duplicated copy of this publication</b-form-text>

                <b-form-invalid-feedback :state="validating && !new_title ? false : null">
                    Title is required
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
                <b-form-radio-group v-model="is_alternate_language">
                    <b-form-radio value="0">Duplicate for same language</b-form-radio>
                    <b-form-radio v-if="selected_publication.language == 'en' || selected_publication.language == 'EN'" value="1">Duplicate for Spanish translation</b-form-radio>
                    <b-form-radio v-if="can_deep_duplicate_publication" value="2">Deep Duplicate</b-form-radio>
                </b-form-radio-group>
            </b-form-group>

            <b-form-group label="Include in duplication">
                <b-form-checkbox v-model="options.include_standards">
                    Include Standards
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_worksheets">
                    Include Printables
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_unit_panels_placeholders">
                    Include Panels
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_unit_group_questions">
                    Include Unit Assessment Questions
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_unit_questions">
                    Include Weekly Assessment Questions
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_student_editions">
                    Include Student Edition
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_teacher_editions">
                    Include Teacher Edition
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_crosswords">
                    Include Crosswords
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_articles">
                    Include Articles
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_article_lesson_plans">
                    Include Lesson Plans
                </b-form-checkbox>
                <b-form-checkbox v-if="!is_spanish_translation" v-model="options.include_vocabulary_definitions">
                    Include Vocabulary And Definitions
                </b-form-checkbox>
                <b-form-checkbox v-model="options.include_designer_notes">
                    Include Designer Notes
                </b-form-checkbox>
                <b-form-text>
                    Crosswords require article vocabulary and definitions.
                </b-form-text>
            </b-form-group>

            <template #modal-footer>
                <div v-if="duplication_submitted" style="width: 100%"><b-spinner style="max-width: 20px; max-height: 20px;" label="Loading..."></b-spinner> Duplication has been submitted. Duplication may take a few minutes.</div>
                <b-button variant="light" @click="cancel">
                    Cancel
                </b-button>
                <b-button variant="info" :disabled="checking_sku || duplication_submitted || !can_duplicate_publication" @click="duplicatePublication">
                    <font-awesome-icon :icon="['fad', 'clone']" fixed-width /> Duplicate
                </b-button>
            </template>

        </b-modal>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import { mapState } from 'vuex'

export default {
    data: () => ({
        publications: [],

        selected_publication: {},

        new_sku: null,
        new_title: null,

        sku_available: null,

        loading_publications: false,
        checking_sku: false,
        is_alternate_language: 0,
        options: {
            include_unit_group_questions: true,
            include_unit_questions: true,
            include_standards: true,
            include_worksheets: true,
            include_student_editions: true,
            include_teacher_editions: true,
            include_articles: true,
            include_article_lesson_plans: true,
            include_vocabulary_definitions: true,
            include_unit_panels_placeholders: true,
            include_crosswords: true,
            include_designer_notes: true
        },
        duplication_submitted: false
    }),
    props: [
        'value',
        'publication'
    ],
    components: {},
    computed: {
        ...mapState({
            validating: state => state.utilities.validating,
            user: state => state.user.user
        }),
        modal_id () {
            let modal_id = 'clone-publication-modal'
            return modal_id
        },
        can_duplicate_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_publication');
        },
        can_deep_duplicate_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'deep_duplicate_publication')
        },
        is_spanish_translation() {
            return this.is_alternate_language == 1
        }
    },
    watch: {
        value: {
            handler (value) {
                if (value == true) {
                    this.getPublications()
                    this.resetOptions()

                    if (this.publication) {
                        this.selected_publication = this.publication
                    }
                }
            }
        },
        is_alternate_language () {
            this.resetOptions()
        },
        'options.include_articles' (value) {
            if (!value) {
                this.options.include_crosswords = false
                this.options.include_article_lesson_plans = false
                this.options.include_designer_notes = false
            } else {
                this.options.include_designer_notes = true
            }
        },
        'options.include_vocabulary_definitions' (value) {
            if (!value) {
                this.options.include_crosswords = false
            }
        },
        'options.include_crosswords' (value) {
            if (value) {
                this.options.include_articles = true
                this.options.include_vocabulary_definitions = true
            }
        },
        'options.include_article_lesson_plans' (value) {
            if (value) {
                this.options.include_articles = true
            }
        }
    },
    methods: {
        defaultOptions () {
            return {
                include_unit_group_questions: true,
                include_unit_questions: true,
                include_standards: true,
                include_worksheets: true,
                include_student_editions: true,
                include_teacher_editions: true,
                include_articles: true,
                include_article_lesson_plans: true,
                include_vocabulary_definitions: true,
                include_unit_panels_placeholders: true,
                include_crosswords: true,
                include_designer_notes: true
            }
        },
        getPublications () {
            this.is_alternate_language = 0;
            this.loading_publications = true

            this.$store.dispatch('getPublications')
            .then(response => {
                this.publications = response.data
                this.loading_publications = false
            })
        },
        duplicatePublication () {
            this.$store.commit('setValidating', true)
            if (!this.validated()) { return false }
            if(this.is_alternate_language == 1){
                var message = "Warning! New article IDs will be created when making a Spanish duplication.";
                this.$swal({
                    html: message,
                    showCancelButton: true,
                    confirmButtonText: 'Duplicate For Spanish',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.confirmDuplicatePublication();
                    }
                })
            }
            else{
                this.confirmDuplicatePublication();
            }
        },
        confirmDuplicatePublication(){
            this.duplication_submitted = true;
            if(this.is_alternate_language == 2){
                this.axios.post('/api/publications/deep_duplicate/' + this.selected_publication.id, {
                    sku: this.new_sku,
                    title: this.new_title,
                    options: this.options
                })
                .then(() => {
                    this.$emit('duplicated')
                    this.duplication_submitted = false;
                })
                .catch(error => {
                    if (error) {
                        console.log(error); this.duplication_submitted = false;
                    }
                })
            }
            else{
                this.axios.post('/api/publications/duplicate/' + this.selected_publication.id, {
                    sku: this.new_sku,
                    title: this.new_title,
                    is_alternate_language: this.is_alternate_language == '1' ? 1 : 0,
                    options: this.options
                })
                .then(() => {
                    this.$emit('duplicated');
                    this.duplication_submitted = false;
                })
                .catch(error => {
                    if (error) {
                        console.log(error); this.duplication_submitted = false;
                    }
                })
            }
        },
        validated () {
            let valid = true

            if (
                !this.selected_publication ||
                this.selected_publication && !this.selected_publication.id
            ) {
                valid = false
            }

            if (!this.new_sku) {
                valid = false
                if (this.$refs['new-sku']) {
                    this.$refs['new-sku'].focus()
                }
            } else if (
                !this.new_title
            ) {
                valid = false
                if (this.$refs['new-title']) {
                    this.$refs['new-title'].focus()
                }
            }

            if (
                !this.sku_available ||
                this.sku_available == false
            ) {
                valid = false
            }

            return valid
        },
        formatSku (sku) {
            if (
                sku && sku.length
            ) {
                this.new_sku = sku.toUpperCase()

                this.checkSkuAvailability(sku)
            } else {
                this.sku_available = null
            }
        },
        checkSkuAvailability: _.debounce(function (sku) {
            this.sku_available = null

            this.checking_sku = true

            if(this.new_sku == '') return

            if (
                sku &&
                sku.length
            ) {
                this.axios.get("/api/publications/skuAvailable/" + sku)
                .then(response => {
                    this.sku_available = response.data

                    this.checking_sku = false
                })
                .catch(error => {
                    this.$swal("Error", error.message, "error")
                })
            }
        }, 1000),
        focusNewSku () {
            if (this.$refs['new-sku']) {
                this.$refs['new-sku'].focus()
            }
        },
        setSelectedPublication (publication) {
            this.selected_publication = publication
        },
        resetOptions () {
            this.options = this.defaultOptions()
        },
        cancel (event) {
            event.preventDefault()

            this.hide()
        },
        hide () {
            this.$emit('hide')
        },
        hidden () {
            this.$store.commit('setValidating', false)

            this.selected_publication = {}

            this.new_sku = null
            this.new_title = null
            this.sku_available = null
            this.resetOptions()

            this.$emit('hide')
        }
    }
}
</script>
